import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { 
  Brain, 
  Cpu, 
  TrendingUp, 
  Zap, 
  Target, 
  ArrowRight,
  Sparkles,
  Globe,
  Activity
} from 'lucide-react';

const customStyles = `
  .gradient-text-robotics {
    background: linear-gradient(90deg,
      #00E0FF,
      #7F00FF,
      #FF00E5,
      #00E0FF
    );
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-flow-robotics 8s linear infinite;
  }

  @keyframes gradient-flow-robotics {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .glow-card {
    background: rgba(10, 5, 30, 0.6);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(127, 0, 255, 0.3);
    transition: all 0.4s ease;
  }

  .glow-card:hover {
    border-color: rgba(0, 224, 255, 0.6);
    box-shadow: 0 0 40px rgba(0, 224, 255, 0.3);
    transform: translateY(-5px);
  }

  .neural-grid {
    background-image: 
      linear-gradient(rgba(127, 0, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(127, 0, 255, 0.05) 1px, transparent 1px);
    background-size: 50px 50px;
  }

  .floating-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.3;
    animation: float-orb 10s infinite ease-in-out;
  }

  @keyframes float-orb {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(50px, -50px); }
  }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: 'rgba(15, 15, 35, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: theme.spacing(2),
    color: 'white',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
  },
  '& .MuiDialogTitle-root': {
    background: 'linear-gradient(90deg, rgba(106, 17, 203, 0.3) 0%, rgba(37, 117, 252, 0.3) 100%)',
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialogContentText-root': {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '1.1rem',
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1, 3, 2, 3),
  }
}));

const SouldribuRoboticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('hero');
  const [showContactModal, setShowContactModal] = useState(false);

  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [visionRef, visionInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [performanceRef, performanceInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [roadmapRef, roadmapInView] = useInView({ threshold: 0.3, triggerOnce: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benchmarkData = [
    {
      task: "SimplerEnv (Zero-Shot Simulation)",
      microsoft: "35.4%",
      souldubu: "71.2%",
      lift: "+101%"
    },
    {
      task: "WidowX (Few-Shot Real-World)",
      microsoft: "67.5%",
      souldubu: "92.0%",
      lift: "+36%"
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        {/* Background Effects */}
        <div className="fixed inset-0 neural-grid opacity-30 pointer-events-none" />
        <div className="floating-orb w-96 h-96 bg-purple-600 top-20 -left-48" />
        <div className="floating-orb w-96 h-96 bg-cyan-600 bottom-20 -right-48" style={{ animationDelay: '2s' }} />

        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-purple-500/20">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="text-2xl font-bold gradient-text-robotics cursor-pointer" onClick={() => navigate('/')}>
              QueenDahyun
            </div>
            <div className="flex gap-6">
              <button 
                onClick={() => navigate('/')}
                className="text-gray-300 hover:text-cyan-400 transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => navigate('/about')}
                className="text-gray-300 hover:text-cyan-400 transition-colors"
              >
                About
              </button>
              <button 
                onClick={() => navigate('/research')}
                className="text-gray-300 hover:text-cyan-400 transition-colors"
              >
                Research
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              variants={fadeInUp}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-900/30 border border-purple-500/30 rounded-full mb-8">
                <Sparkles className="text-cyan-400" size={20} />
                <span className="text-sm text-gray-300">Introducing Souldubu Robotics</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                From Digital Mastery to<br />
                <span className="gradient-text-robotics">Physical Embodiment</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
                The Next Leap Towards AGI
              </p>
              
              <p className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto">
                We built the most powerful AI for the digital world. Now, we are giving it hands.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-full font-semibold hover:from-purple-500 hover:to-cyan-500 transition-all transform hover:scale-105 flex items-center justify-center gap-2">
                  Explore Technology
                  <ArrowRight size={20} />
                </button>
                <button 
                  onClick={() => navigate('/research')}
                  className="px-8 py-4 bg-white/5 border border-white/10 rounded-full font-semibold hover:bg-white/10 transition-all"
                >
                  View Research
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Digital Foundation Section */}
        <section className="relative py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeInUp}
                className="text-4xl md:text-5xl font-bold mb-6 text-center"
              >
                Our Foundation: <span className="gradient-text-robotics">Souldubu Digital</span>
              </motion.h2>
              
              <motion.p 
                variants={fadeInUp}
                className="text-xl text-gray-300 text-center mb-16 max-w-4xl mx-auto"
              >
                A 7-Billion parameter VLA model designed to operate any software, application, or website just as a human would.
              </motion.p>

              <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-6 mb-12">
                <div className="glow-card rounded-2xl p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                    <Target className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">59.5%</h3>
                  <p className="text-gray-400">Success rate on Osworld benchmark</p>
                </div>

                <div className="glow-card rounded-2xl p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                    <Cpu className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">7B Parameters</h3>
                  <p className="text-gray-400">Most efficient digital agent in the world</p>
                </div>

                <div className="glow-card rounded-2xl p-8">
                  <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                    <TrendingUp className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Rivals Claude 4.5</h3>
                  <p className="text-gray-400">Performance at fraction of compute cost</p>
                </div>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="bg-gradient-to-r from-purple-900/20 to-cyan-900/20 border border-purple-500/30 rounded-2xl p-8"
              >
                <p className="text-lg text-gray-300 leading-relaxed">
                  <strong className="text-white">Parameter-for-parameter</strong>, Souldubu (7B) is the most efficient and powerful digital agent in the world. Our results rival models like Claude 4.5 Sonnet (61.2%), presumed to be orders of magnitude larger, while delivering state-of-the-art performance at a fraction of the computational cost.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Vision Section */}
        <section ref={visionRef} className="relative py-20 px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              animate={visionInView ? "visible" : "hidden"}
              variants={fadeInUp}
            >
              <div className="flex items-center justify-center gap-3 mb-8">
                <Brain className="text-cyan-400" size={40} />
                <h2 className="text-4xl md:text-5xl font-bold gradient-text-robotics">
                  The Grand Vision
                </h2>
              </div>
              
              <p className="text-xl text-gray-300 text-center mb-12 max-w-4xl mx-auto leading-relaxed">
                Unifying Digital and Physical Intelligence
              </p>

              <div className="glow-card rounded-2xl p-10 mb-8">
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  Our success in the digital world was only the first step. We believe the path to true <strong className="text-cyan-400">Artificial General Intelligence (AGI)</strong> does not lie in digital <em>or</em> physical tasks alone, but in their <strong className="text-purple-400">unification</strong>.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  An AI that learns to navigate a complex website is developing the same core cognitive skills needed to navigate a kitchen: spatial reasoning, goal-oriented planning, and precise action execution. The underlying intelligence is the same.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-900/30 to-transparent border border-purple-500/20 rounded-2xl p-8">
                  <Globe className="text-purple-400 mb-4" size={32} />
                  <h3 className="text-2xl font-bold mb-3">Digital Domain</h3>
                  <p className="text-gray-400">Master complex software, websites, and digital interfaces with human-like understanding</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-900/30 to-transparent border border-cyan-500/20 rounded-2xl p-8">
                  <Activity className="text-cyan-400 mb-4" size={32} />
                  <h3 className="text-2xl font-bold mb-3">Physical Domain</h3>
                  <p className="text-gray-400">Execute precise physical actions with spatial reasoning and manipulation capabilities</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Souldubu Robot Section */}
        <section ref={performanceRef} className="relative py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              animate={performanceInView ? "visible" : "hidden"}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="text-center mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-900/30 border border-cyan-500/30 rounded-full mb-8">
                  <Zap className="text-cyan-400" size={20} />
                  <span className="text-sm text-gray-300">Now Available</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Introducing <span className="gradient-text-robotics">Souldubu Robot</span>
                </h2>
                
                <p className="text-xl text-gray-300 max-w-4xl mx-auto">
                  The Brain for the Physical World
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="glow-card rounded-2xl p-10 mb-12">
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  This is not a new model built from scratch. It is our proven, battle-tested Souldubu (7B) core, now extensively trained on the massive <strong className="text-cyan-400">Open-X-Embodiment dataset</strong>.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Souldubu Robot is designed to be a universal "brain" for robotics—a single, general-purpose model that can be deployed onto any hardware.
                </p>
              </motion.div>

              {/* Performance Table */}
              <motion.div variants={fadeInUp} className="mb-8">
                <h3 className="text-3xl font-bold mb-6 text-center">
                  Preliminary Performance: <span className="gradient-text-robotics">A New State-of-the-Art</span>
                </h3>
                
                <div className="bg-gradient-to-br from-purple-900/20 to-cyan-900/20 border border-purple-500/30 rounded-2xl p-8 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left py-4 px-4 text-gray-300">Benchmark Task</th>
                        <th className="text-center py-4 px-4 text-gray-300">Microsoft Magma-8B</th>
                        <th className="text-center py-4 px-4 text-cyan-400 font-bold">Souldubu Robot (7B)</th>
                        <th className="text-center py-4 px-4 text-gray-300">Improvement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {benchmarkData.map((row, idx) => (
                        <tr key={idx} className="border-b border-purple-500/10">
                          <td className="py-4 px-4 text-gray-300">{row.task}</td>
                          <td className="py-4 px-4 text-center text-gray-400">{row.microsoft}</td>
                          <td className="py-4 px-4 text-center text-cyan-400 font-bold text-lg">{row.souldubu}</td>
                          <td className="py-4 px-4 text-center text-green-400 font-semibold">{row.lift}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-sm text-gray-500 mt-4 italic">
                    *Note: Data from internal preliminary inference, subject to full academic benchmarking.
                  </p>
                </div>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="bg-gradient-to-r from-green-900/20 to-cyan-900/20 border border-green-500/30 rounded-2xl p-8"
              >
                <p className="text-lg text-gray-300 leading-relaxed">
                  These early results are phenomenal. They indicate that Souldubu Robot possesses a level of spatial reasoning and action-generation ability that is fundamentally more advanced than its peers, even those with larger parameter counts.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Bottleneck & Opportunity Section */}
        <section className="relative py-20 px-6 bg-gradient-to-b from-transparent via-cyan-900/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
                The Real <span className="gradient-text-robotics">Bottleneck</span>
              </h2>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="glow-card rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-4 text-red-400">The Challenge</h3>
                  <p className="text-gray-300 leading-relaxed">
                    The primary bottleneck for physical AGI is no longer the AI; it is the <strong className="text-white">hardware</strong>. The physical world demands dynamic flexibility, fine-motor control, and sensory feedback that modern robotic platforms are only beginning to achieve.
                  </p>
                </div>

                <div className="glow-card rounded-2xl p-8">
                  <h3 className="text-2xl font-bold mb-4 text-green-400">The Opportunity</h3>
                  <p className="text-gray-300 leading-relaxed">
                    This "embodiment problem" presents a massive market opportunity. As robotic hardware improves, the demand for a powerful, hardware-agnostic, general-purpose AI brain will be universal. <strong className="text-white">We are building that brain today.</strong>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Roadmap Section */}
        <section ref={roadmapRef} className="relative py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              animate={roadmapInView ? "visible" : "hidden"}
              variants={fadeInUp}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center">
                Our <span className="gradient-text-robotics">Roadmap</span> & Invitation
              </h2>

              <div className="glow-card rounded-2xl p-10 mb-8">
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  The experimental results from Souldubu Robot are monumental. We have validated our core hypothesis: a world-class digital agent is the single best foundation for a world-class physical agent.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  We are now entering a phase of extensive research, continuous learning, and scaling. Our work in mastering the digital world will continue to fuel our advancements in the physical, creating a feedback loop that accelerates us toward true AGI.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-purple-500/30 rounded-2xl p-10 text-center">
                <h3 className="text-3xl font-bold mb-6 gradient-text-robotics">Join Us on This Frontier</h3>
                <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
                  We invite researchers, partners, and investors to join us. We are not just building a better AI; we are building the intelligence that will power the next generation of industry, automation, and human potential.
                </p>
                <button 
                  onClick={() => setShowContactModal(true)}
                  className="px-10 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-full font-semibold text-lg hover:from-purple-500 hover:to-cyan-500 transition-all transform hover:scale-105"
                >
                  Get In Touch
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Contact Modal - Updated Styling */}
        <StyledDialog
          open={showContactModal}
          onClose={() => setShowContactModal(false)}
          aria-labelledby="contact-dialog-title"
          aria-describedby="contact-dialog-description"
        >
          <DialogTitle id="contact-dialog-title">
            Contact Information
            <IconButton
              aria-label="close"
              onClick={() => setShowContactModal(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="contact-dialog-description" sx={{ mt: 2 }}>
              Please reach out to us via the following email address:
            </DialogContentText>
            <Typography variant="h6" sx={{ mt: 1, mb: 2, color: '#2575fc', userSelect: 'all' }}>
              md.sofiullah@queendahyun.com
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowContactModal(false)}
              variant="outlined"
              sx={{ 
                borderColor: 'rgba(255, 255, 255, 0.3)', 
                color: '#fff', 
                '&:hover': { 
                  borderColor: 'rgba(255, 255, 255, 0.6)', 
                  background: 'rgba(255, 255, 255, 0.05)' 
                } 
              }}
            >
              Close
            </Button>
          </DialogActions>
        </StyledDialog>

        {/* Footer */}
        <footer className="relative py-12 px-6 border-t border-purple-500/20">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-400">
              © 2025 QueenDahyun. Building the future of AGI.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SouldribuRoboticsPage;